import React, { useState, useEffect } from 'react';
import { API, authHeaders } from '../services/apiConfig';
import { FaRupeeSign, FaTimes, FaPlus, FaMapMarkerAlt, FaCreditCard, FaMobileAlt, FaMoneyBillWave } from 'react-icons/fa';
import './Checkout.css';

const fmtAmt = (n) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Checkout({ user, onClose, onOrderPlaced }) {
  const [cartItems, setCartItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [step, setStep] = useState(1); // 1=address, 2=payment, 3=confirm
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newAddr, setNewAddr] = useState({ fullName: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', isDefaultShipping: false });
  const [showNewAddr, setShowNewAddr] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      fetch(`${API}/api/cart/user/${user.userId}`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/cart/user/${user.userId}/summary`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/orders/address/user/${user.userId}`, { headers: authHeaders() }).then(r => r.json()),
    ]).then(([items, sum, addrs]) => {
      setCartItems(items);
      setSummary(sum);
      setAddresses(addrs);
      const def = addrs.find(a => a.isDefaultShipping) || addrs[0];
      if (def) setSelectedAddress(def);
    }).catch(() => setError('Failed to load checkout data'));
  }, [user]);

  const shipping = summary ? summary.subtotal * 0.08 : 0;
  const grandTotal = summary ? summary.subtotal + summary.tax + shipping : 0;

  const saveAddress = async () => {
    if (!newAddr.fullName || !newAddr.phone || !newAddr.addressLine1 || !newAddr.city || !newAddr.state || !newAddr.pincode) {
      setError('Please fill all required address fields'); return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/orders/address/user/${user.userId}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ...newAddr, userId: user.userId })
      });
      const saved = await res.json();
      setAddresses(prev => [...prev, saved]);
      setSelectedAddress(saved);
      setShowNewAddr(false);
      setNewAddr({ fullName: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', isDefaultShipping: false });
    } catch { setError('Failed to save address'); }
    finally { setLoading(false); }
  };

  const placeOrder = async () => {
    if (!selectedAddress) { setError('Please select a delivery address'); return; }
    setLoading(true); setError('');
    try {
      const addrStr = `${selectedAddress.fullName}, ${selectedAddress.addressLine1}${selectedAddress.addressLine2 ? ', ' + selectedAddress.addressLine2 : ''}, ${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.pincode}, Ph: ${selectedAddress.phone}`;
      const items = cartItems.map(i => ({ productId: i.productId, productName: i.productName, quantity: i.cartQuantity, price: i.price, variantColor: i.variantColor, variantSize: i.variantSize }));
      const orderRes = await fetch(`${API}/api/orders`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          userId: user.userId, totalAmount: grandTotal,
          shippingAddress: addrStr, items: JSON.stringify(items),
          fullName: selectedAddress.fullName, phone: selectedAddress.phone,
          email: user.email || '', paymentMethod
        })
      });
      if (!orderRes.ok) throw new Error('Order creation failed');
      const order = await orderRes.json();

      await fetch(`${API}/api/payment/process`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ userId: user.userId, orderId: order.id, amount: grandTotal, paymentMethod })
      });

      await fetch(`${API}/api/cart/user/${user.userId}`, { method: 'DELETE', headers: authHeaders() });

      if (onOrderPlaced) onOrderPlaced(order);
      setStep(4);
    } catch (e) { setError(e.message || 'Failed to place order'); }
    finally { setLoading(false); }
  };

  if (!user) return null;

  return (
    <div className="chk-overlay" onClick={onClose}>
      <div className="chk-modal" onClick={e => e.stopPropagation()}>
        <div className="chk-header">
          <h2>Checkout</h2>
          <button className="chk-close" onClick={onClose}><FaTimes /></button>
        </div>

        {step === 4 ? (
          <div className="chk-success">
            <div className="chk-success-icon">✓</div>
            <h3>Order Placed Successfully!</h3>
            <p>Thank you for your order. You'll receive a confirmation shortly.</p>
            <button className="chk-btn-primary" onClick={onClose}>Continue Shopping</button>
          </div>
        ) : (
          <div className="chk-body">
            <div className="chk-steps">
              <span className={step >= 1 ? 'active' : ''}>1. Address</span>
              <span className={step >= 2 ? 'active' : ''}>2. Payment</span>
              <span className={step >= 3 ? 'active' : ''}>3. Confirm</span>
            </div>

            {error && <div className="chk-error">{error}</div>}

            {/* Step 1: Address */}
            {step === 1 && (
              <div className="chk-section">
                <h3><FaMapMarkerAlt /> Delivery Address</h3>
                {addresses.map(addr => (
                  <div key={addr.id} className={`chk-addr-card ${selectedAddress?.id === addr.id ? 'selected' : ''}`} onClick={() => setSelectedAddress(addr)}>
                    <div className="chk-radio">{selectedAddress?.id === addr.id ? '●' : '○'}</div>
                    <div>
                      <strong>{addr.fullName}</strong> · {addr.phone}<br />
                      {addr.addressLine1}{addr.addressLine2 ? ', ' + addr.addressLine2 : ''}<br />
                      {addr.city}, {addr.state} - {addr.pincode}
                    </div>
                  </div>
                ))}
                {!showNewAddr && (
                  <button className="chk-btn-outline" onClick={() => setShowNewAddr(true)}><FaPlus /> Add New Address</button>
                )}
                {showNewAddr && (
                  <div className="chk-new-addr">
                    {['fullName', 'phone', 'addressLine1', 'addressLine2', 'city', 'state', 'pincode'].map(f => (
                      <input key={f} placeholder={f === 'addressLine1' ? 'Address Line 1 *' : f === 'addressLine2' ? 'Address Line 2' : f.charAt(0).toUpperCase() + f.slice(1) + (f === 'addressLine2' ? '' : ' *')}
                        value={newAddr[f]} onChange={e => setNewAddr(p => ({ ...p, [f]: e.target.value }))} />
                    ))}
                    <div className="chk-addr-btns">
                      <button className="chk-btn-primary" onClick={saveAddress} disabled={loading}>Save Address</button>
                      <button className="chk-btn-outline" onClick={() => setShowNewAddr(false)}>Cancel</button>
                    </div>
                  </div>
                )}
                <button className="chk-btn-primary chk-next" onClick={() => { if (!selectedAddress) { setError('Select an address'); return; } setError(''); setStep(2); }} disabled={!selectedAddress}>Continue to Payment</button>
              </div>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <div className="chk-section">
                <h3><FaCreditCard /> Payment Method</h3>
                {[['COD', <FaMoneyBillWave />, 'Cash on Delivery'], ['UPI', <FaMobileAlt />, 'UPI'], ['CREDIT_CARD', <FaCreditCard />, 'Credit Card'], ['DEBIT_CARD', <FaCreditCard />, 'Debit Card']].map(([val, icon, label]) => (
                  <div key={val} className={`chk-pay-card ${paymentMethod === val ? 'selected' : ''}`} onClick={() => setPaymentMethod(val)}>
                    <div className="chk-radio">{paymentMethod === val ? '●' : '○'}</div>
                    {icon} <span>{label}</span>
                  </div>
                ))}
                <div className="chk-nav-btns">
                  <button className="chk-btn-outline" onClick={() => setStep(1)}>Back</button>
                  <button className="chk-btn-primary" onClick={() => { setError(''); setStep(3); }}>Review Order</button>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 3 && summary && (
              <div className="chk-section">
                <h3>Order Summary</h3>
                <div className="chk-items-list">
                  {cartItems.map(item => (
                    <div key={item.cartId} className="chk-item-row">
                      <span>{item.productName} {item.variantColor ? `(${item.variantColor})` : ''} × {item.cartQuantity}</span>
                      <span><FaRupeeSign />{fmtAmt(item.price * item.cartQuantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="chk-totals">
                  <div className="chk-total-row"><span>Subtotal</span><span><FaRupeeSign />{fmtAmt(summary.subtotal)}</span></div>
                  <div className="chk-total-row"><span>GST (18%)</span><span><FaRupeeSign />{fmtAmt(summary.tax)}</span></div>
                  <div className="chk-total-row"><span>Shipping (8%)</span><span><FaRupeeSign />{fmtAmt(shipping)}</span></div>
                  <div className="chk-total-row chk-grand"><span>Total Payable</span><span><FaRupeeSign />{fmtAmt(grandTotal)}</span></div>
                </div>
                <div className="chk-confirm-info">
                  <p><strong>Deliver to:</strong> {selectedAddress?.fullName}, {selectedAddress?.addressLine1}, {selectedAddress?.city}</p>
                  <p><strong>Payment:</strong> {paymentMethod.replace('_', ' ')}</p>
                </div>
                <div className="chk-nav-btns">
                  <button className="chk-btn-outline" onClick={() => setStep(2)}>Back</button>
                  <button className="chk-btn-primary" onClick={placeOrder} disabled={loading}>{loading ? 'Placing Order...' : `Place Order · ₹${fmtAmt(grandTotal)}`}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
